import React, { useEffect, useState, useMemo } from "react";
import { Input } from "../../components/ui/input";
import { Separator } from "../../components/ui/separator";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";

const Enquiry = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    const fetchEnquiries = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("adminToken");
        const res = await axios.get(
          "https://wavetechservices.in/api/admin/contactus",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setEnquiries(res.data.data || []);
      } catch (err) {
        console.error("Error fetching enquiries:", err);
        setError("Failed to load enquiries. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchEnquiries();
  }, []);

  // Filter enquiries based on search term
  const filteredEnquiries = useMemo(() => {
    if (!searchTerm) return enquiries;
    
    const term = searchTerm.toLowerCase();
    return enquiries.filter(enquiry => 
      enquiry.fullName?.toLowerCase().includes(term) ||
      enquiry.email?.toLowerCase().includes(term) ||
      enquiry.phone?.toString().includes(term) ||
      enquiry.message?.toLowerCase().includes(term)
    );
  }, [enquiries, searchTerm]);

  // Pagination logic
  const totalPages = Math.ceil(filteredEnquiries.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEnquiries.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  // Generate visible page numbers
  const visiblePageNumbers = useMemo(() => {
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust if we're at the start or end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }, [currentPage, totalPages]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (isLoading) {
    return <div className="p-4 text-center">Loading enquiries...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-500">{error}</div>;
  }

  return (
    <div className="px-[1vw] my-5">
      <div className="flex lg:justify-between lg:items-center flex-col lg:flex-row">
        <h1 className="text-2xl">Customer Enquiries</h1>
        <div className="flex lg:gap-4 items-center gap-2 my-3 lg:my-0">
          <Input
            type="text"
            placeholder="Search by name, email, phone or message"
            className="border p-2 rounded-md w-full max-w-md"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <Separator className="my-4" />

      <div className="overflow-x-auto lg:max-w-[1000px] max-w-[380px] md:max-w-[580px] lg:h-[65vh] h-[58vh] md:h-[70vh]">
        {filteredEnquiries.length === 0 ? (
          <div className="text-center p-4">
            {searchTerm ? "No matching enquiries found" : "No enquiries available"}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Sl No.</TableHead>
                <TableHead className="min-w-[150px]">Customer Name</TableHead>
                <TableHead className="min-w-[120px]">Phone No</TableHead>
                <TableHead className="min-w-[200px]">Email</TableHead>
                <TableHead className="min-w-[300px]">Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentItems.map((enquiry, index) => (
                <TableRow key={enquiry._id || index}>
                  <TableCell>{indexOfFirstItem + index + 1}.</TableCell>
                  <TableCell>{enquiry.fullName || "-"}</TableCell>
                  <TableCell>{enquiry.phone || "-"}</TableCell>
                  <TableCell>{enquiry.email || "-"}</TableCell>
                  <TableCell className="max-w-[300px] truncate">
                    {enquiry.message || "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
            </PaginationItem>

            {visiblePageNumbers[0] > 1 && (
              <>
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(1)}>
                    1
                  </PaginationLink>
                </PaginationItem>
                {visiblePageNumbers[0] > 2 && (
                  <PaginationItem>
                    <span className="px-2">...</span>
                  </PaginationItem>
                )}
              </>
            )}

            {visiblePageNumbers.map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  isActive={page === currentPage}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}

            {visiblePageNumbers[visiblePageNumbers.length - 1] < totalPages && (
              <>
                {visiblePageNumbers[visiblePageNumbers.length - 1] < totalPages - 1 && (
                  <PaginationItem>
                    <span className="px-2">...</span>
                  </PaginationItem>
                )}
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              </>
            )}

            <PaginationItem>
              <PaginationNext
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};

export default Enquiry;